import { Navigation } from '../components/Navigation';
import { CTAFooter } from '../components/CTAFooter';

export default function PrivacyPolicyPage() {
  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      <main className="pt-24 pb-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 lowercase">

          <h1 className="text-3xl font-bold text-foreground mb-2">Privacy Policy</h1>
          <p className="text-muted-foreground mb-8"><strong>Effective Date:</strong> April 4, 2026</p>

          <p className="text-muted-foreground leading-relaxed mb-6">
            This Privacy Policy describes how Discgo LLC ("Discgo," "we," "us," or "our") collects, uses,
            and shares information about you when you use our website located at www.godiscgo.com and our
            booking platform services (collectively, the "Service"). By using the Service, you agree to the
            collection and use of information in accordance with this policy.
          </p>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-foreground mt-10 mb-3">1. Information We Collect</h2>

            <h3 className="text-base font-semibold text-foreground mt-6 mb-2">1.1 Information You Provide Directly</h3>
            <p className="text-muted-foreground leading-relaxed mb-3">When you create an account or use our Service, we may collect:</p>
            <ul className="list-disc pl-6 space-y-2 mb-4 text-muted-foreground">
              <li>Full name and email address</li>
              <li>Username and password (stored in encrypted form)</li>
              <li>Payment information (processed securely through Stripe; we do not store full card details on our servers)</li>
              <li>Booking details, including course information and scheduling preferences</li>
              <li>Communications you send us, including support requests</li>
            </ul>

            <h3 className="text-base font-semibold text-foreground mt-6 mb-2">1.2 Information Collected Automatically</h3>
            <p className="text-muted-foreground leading-relaxed mb-3">When you access or use our Service, we automatically collect certain information, including:</p>
            <ul className="list-disc pl-6 space-y-2 mb-4 text-muted-foreground">
              <li>Device information (browser type, operating system, device identifiers)</li>
              <li>Log data (IP address, pages visited, time and date of visits, referring URLs)</li>
              <li>Usage data and interactions with our platform</li>
              <li>Cookies and similar tracking technologies (see Section 5)</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-foreground mt-10 mb-3">2. How We Use Your Information</h2>
            <p className="text-muted-foreground leading-relaxed mb-3">We use the information we collect to:</p>
            <ul className="list-disc pl-6 space-y-2 mb-4 text-muted-foreground">
              <li>Create and manage your account</li>
              <li>Process bookings and facilitate payments</li>
              <li>Communicate with you about your account, bookings, and our Service</li>
              <li>Send transactional emails (e.g., booking confirmations, receipts)</li>
              <li>Improve, personalize, and develop our Service</li>
              <li>Detect, investigate, and prevent fraudulent or unauthorized activity</li>
              <li>Comply with applicable legal obligations</li>
              <li>Respond to your questions and provide customer support</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-foreground mt-10 mb-3">3. How We Share Your Information</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">We do not sell your personal information. We may share your information in the following circumstances:</p>

            <h3 className="text-base font-semibold text-foreground mt-6 mb-2">3.1 Service Providers</h3>
            <p className="text-muted-foreground leading-relaxed mb-3">We share information with trusted third-party vendors who help us operate our platform, including:</p>
            <ul className="list-disc pl-6 space-y-2 mb-4 text-muted-foreground">
              <li>Stripe (payment processing) — subject to Stripe's own Privacy Policy</li>
              <li>Cloud hosting and infrastructure providers</li>
            </ul>

            <h3 className="text-base font-semibold text-foreground mt-6 mb-2">3.2 Between Users</h3>
            <p className="text-muted-foreground leading-relaxed mb-4">
              When a player makes a booking, relevant booking details (such as name and contact information)
              may be shared with the course owner to fulfill the booking, and vice versa.
            </p>

            <h3 className="text-base font-semibold text-foreground mt-6 mb-2">3.3 Legal Requirements</h3>
            <p className="text-muted-foreground leading-relaxed mb-4">
              We may disclose your information if required by law, court order, or governmental authority,
              or to protect the rights, property, or safety of Discgo, our users, or the public.
            </p>

            <h3 className="text-base font-semibold text-foreground mt-6 mb-2">3.4 Business Transfers</h3>
            <p className="text-muted-foreground leading-relaxed mb-4">
              If Discgo is involved in a merger, acquisition, or sale of assets, your information may be
              transferred as part of that transaction. We will notify you via email or prominent notice on
              our website of any change in ownership.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-foreground mt-10 mb-3">4. Data Retention</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              We retain your personal information for as long as your account is active or as needed to
              provide you with the Service. We may also retain information as required by law, to resolve
              disputes, or to enforce our agreements. You may request deletion of your account and associated
              data by contacting us at{' '}
              <a href="mailto:discgoinfo@gmail.com" className="text-primary hover:underline">discgoinfo@gmail.com</a>.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-foreground mt-10 mb-3">5. Cookies and Tracking Technologies</h2>
            <p className="text-muted-foreground leading-relaxed mb-3">
              We use cookies and similar technologies to operate and improve our Service. Cookies are small
              data files stored on your browser. We use:
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-4 text-muted-foreground">
              <li><strong className="text-foreground">Essential cookies:</strong> necessary for the Service to function (e.g., session management, authentication)</li>
              <li><strong className="text-foreground">Analytics cookies:</strong> help us understand how users interact with the Service</li>
              <li><strong className="text-foreground">Preference cookies:</strong> remember your settings and preferences</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed mb-4">
              You can control cookie settings through your browser settings. Note that disabling certain
              cookies may impact the functionality of the Service.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-foreground mt-10 mb-3">6. Data Security</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              We implement reasonable technical and organizational measures to protect your information
              against unauthorized access, loss, or misuse. Payment data is handled exclusively by Stripe,
              a PCI-compliant payment processor. However, no method of transmission over the internet or
              electronic storage is 100% secure, and we cannot guarantee absolute security.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-foreground mt-10 mb-3">7. Children's Privacy</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              Our Service is not directed to individuals under the age of 13. We do not knowingly collect
              personal information from children under 13. If we become aware that we have collected personal
              information from a child under 13, we will take steps to delete that information promptly.
              If you believe we have collected such information, please contact us at{' '}
              <a href="mailto:discgoinfo@gmail.com" className="text-primary hover:underline">discgoinfo@gmail.com</a>.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-foreground mt-10 mb-3">8. Your Rights and Choices</h2>
            <p className="text-muted-foreground leading-relaxed mb-3">You may have certain rights regarding your personal information, including:</p>
            <ul className="list-disc pl-6 space-y-2 mb-4 text-muted-foreground">
              <li><strong className="text-foreground">Access:</strong> Request a copy of the personal information we hold about you</li>
              <li><strong className="text-foreground">Correction:</strong> Request that we correct inaccurate or incomplete information</li>
              <li><strong className="text-foreground">Deletion:</strong> Request deletion of your personal information, subject to certain exceptions</li>
              <li><strong className="text-foreground">Opt-out:</strong> Unsubscribe from marketing emails at any time by clicking the unsubscribe link</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed mb-4">
              To exercise any of these rights, please contact us at{' '}
              <a href="mailto:discgoinfo@gmail.com" className="text-primary hover:underline">discgoinfo@gmail.com</a>.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-foreground mt-10 mb-3">9. Third-Party Links</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              Our Service may contain links to third-party websites or services. We are not responsible for
              the privacy practices of those third parties. We encourage you to review the privacy policies
              of any third-party sites you visit.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-foreground mt-10 mb-3">10. Changes to This Privacy Policy</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              We may update this Privacy Policy from time to time. When we make material changes, we will
              notify you by updating the effective date at the top of this policy and, where appropriate,
              by sending you an email notification. Your continued use of the Service after changes become
              effective constitutes your acceptance of the updated policy.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-foreground mt-10 mb-3">11. Contact Us</h2>
            <p className="text-muted-foreground leading-relaxed mb-3">If you have any questions, concerns, or requests regarding this Privacy Policy, please contact us at:</p>
            <address className="not-italic text-muted-foreground leading-relaxed">
              <strong className="text-foreground">Discgo LLC</strong><br />
              <a href="https://www.godiscgo.com" className="text-primary hover:underline">www.godiscgo.com</a><br />
              <a href="mailto:discgoinfo@gmail.com" className="text-primary hover:underline">discgoinfo@gmail.com</a>
            </address>
          </section>

        </div>
      </main>
      <CTAFooter />
    </div>
  );
}
